import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, Grid, IconButton, Link, Typography } from "@mui/material";
import { format, isValid, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { TInvoiceClient } from "./FinancialHealthListPresentation";
import { globalMaskDocument, globalTranslateStatusInvoices } from "../../../shared/utils";

interface ModalDetailInvoiceClientProps {
	invoiceRow?: TInvoiceClient | null;
	isOpenModal: boolean;
	onCloseModal: () => void;
}

const formatDateOnly = (value?: string | null) => {
	if (!value) return "-";
	const rawValue = String(value).trim();
	if (!rawValue) return "-";

	const dateSlice = rawValue.length >= 10 ? rawValue.slice(0, 10) : rawValue;
	const parsedDate = parseISO(dateSlice);
	if (!isValid(parsedDate)) return "-";

	return format(parsedDate, "dd/MM/yyyy", { locale: ptBR });
};

const formatCurrency = (value?: string | number | null) => {
	const numericValue = Number(value ?? 0);
	if (!Number.isFinite(numericValue)) return "-";
	return numericValue.toLocaleString("pt-br", { style: "currency", currency: "BRL" });
};

const statusChipStyles = (status?: string) => {
	switch (status) {
		case "paid":
			return { color: "#148f77", background: "#d9f5ef" };
		case "open":
			return { color: "#3761eb", background: "#e0ecff" };
		case "pending":
			return { color: "#8f9196", background: "#efefef" };
		case "canceled":
			return { color: "#d73a49", background: "#fdebec" };
		default:
			return { color: "#4b5563", background: "#f3f4f6" };
	}
};

const SectionTitle = ({ title, subtitle }: { title: string; subtitle: string }) => (
	<Box mb={1.5}>
		<Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827" }}>
			{title}
		</Typography>
		<Typography variant="body2" sx={{ color: "#6b7280" }}>
			{subtitle}
		</Typography>
	</Box>
);

const DetailItem = ({ label, value }: { label: string; value?: string }) => (
	<Box sx={{ py: 0.75 }}>
		<Typography variant="caption" sx={{ color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.3 }}>
			{label}
		</Typography>
		<Typography variant="body2" sx={{ color: "#111827", fontWeight: 600, wordBreak: "break-word" }}>
			{value || "-"}
		</Typography>
	</Box>
);

export const ModalDetailInvoiceClient: React.FC<ModalDetailInvoiceClientProps> = ({ invoiceRow, isOpenModal, onCloseModal }) => {
	if (!invoiceRow) return null;

	const currentStatusStyles = statusChipStyles(invoiceRow.status);
	const installmentsLabel = String(invoiceRow.installmentsNumber ?? invoiceRow.installments_number ?? "-");
	const invoiceLink = invoiceRow.url || invoiceRow.summaryUrl || invoiceRow.sicoobPdfBoleto || "";

	return (
		<Dialog open={isOpenModal} onClose={onCloseModal} fullWidth maxWidth="md">
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
				<Typography variant="h6" color="primary">
					Detalhes do boleto
				</Typography>
				<IconButton onClick={onCloseModal} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				<Box
					sx={{
						background: "linear-gradient(135deg, rgba(1,112,179,0.10) 0%, rgba(1,112,179,0.03) 100%)",
						border: "1px solid rgba(1,112,179,0.12)",
						borderRadius: "16px",
						p: { xs: 2, md: 2.5 },
						mb: 3,
					}}
				>
					<Box display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} flexDirection={{ xs: "column", md: "row" }} gap={2}>
						<Box>
							<Typography variant="h6" sx={{ fontWeight: 700, color: "#111827" }}>
								{invoiceRow.clientName || "-"}
							</Typography>
							<Typography variant="body2" sx={{ color: "#6b7280" }}>
								{globalMaskDocument(invoiceRow.clientDocument || "")} | {invoiceRow.email || "-"}
							</Typography>
						</Box>
						<Box display="flex" alignItems={{ xs: "flex-start", md: "center" }} gap={1.5} flexDirection={{ xs: "column", md: "row" }}>
							<Chip
								label={globalTranslateStatusInvoices(invoiceRow.status)}
								sx={{
									color: currentStatusStyles.color,
									background: currentStatusStyles.background,
									fontWeight: 700,
								}}
							/>
							<Box sx={{ px: 2, py: 1.1, borderRadius: "999px", bgcolor: "primary.main", color: "#fff", minWidth: 170, textAlign: "center" }}>
								<Typography
									variant="h6"
									sx={{
										fontWeight: 700,
										fontFamily: "inherit",
										lineHeight: 1.1,
										letterSpacing: 0,
										fontFeatureSettings: '"tnum"',
									}}
								>
									{formatCurrency(invoiceRow.value)}
								</Typography>
							</Box>
						</Box>
					</Box>

					<Grid container spacing={2} sx={{ mt: 1.5 }}>
						<Grid item xs={12} md={3}>
							<DetailItem label="Referência" value={invoiceRow.reference || "-"} />
						</Grid>
						<Grid item xs={12} md={3}>
							<DetailItem label="Vencimento" value={formatDateOnly(invoiceRow.dueDate)} />
						</Grid>
						<Grid item xs={12} md={3}>
							<DetailItem label="Pagamento" value={formatDateOnly(invoiceRow.paymentDate)} />
						</Grid>
						<Grid item xs={12} md={3}>
							<DetailItem label="Recebimento" value={formatDateOnly(invoiceRow.creditDate)} />
						</Grid>
					</Grid>
				</Box>

				<Grid container spacing={3}>
					<Grid item xs={12} md={6}>
						<SectionTitle title="Identificação" subtitle="Dados operacionais e de integração do boleto" />
						<DetailItem label="ID" value={invoiceRow.id} />
						<DetailItem label="Client ID" value={invoiceRow.clientId || "-"} />
						<DetailItem label="Platform ID" value={invoiceRow.platformId || "-"} />
						<DetailItem label="Gateway" value={invoiceRow.gateway || "-"} />
						<DetailItem label="Parcelas" value={installmentsLabel} />
						<DetailItem label="Nosso número" value={invoiceRow.sicoobSeuNumero || "-"} />
					</Grid>

					<Grid item xs={12} md={6}>
						<SectionTitle title="Valores" subtitle="Composição financeira do boleto" />
						<DetailItem label="Valor nominal" value={formatCurrency(invoiceRow.value)} />
						<DetailItem label="Desconto" value={formatCurrency(invoiceRow.discount)} />
						<DetailItem label="Acréscimo" value={formatCurrency(invoiceRow.addition)} />
						<DetailItem label="Valor pago" value={formatCurrency(invoiceRow.amountPaid)} />
						<DetailItem label="Valor recebido" value={formatCurrency(invoiceRow.creditAmount)} />
						<DetailItem label="Taxas da plataforma" value={formatCurrency(invoiceRow.platformTaxes)} />
					</Grid>

					<Grid item xs={12} md={6}>
						<SectionTitle title="Ocorrências" subtitle="Cancelamento, baixa e movimentações do título" />
						<DetailItem label="Motivo do cancelamento" value={invoiceRow.canceledReason || "-"} />
						<DetailItem label="Tipo do cancelamento" value={invoiceRow.canceledType || "-"} />
						<DetailItem label="Usuário do cancelamento" value={invoiceRow.canceledUser || "-"} />
						<DetailItem label="Cancelado em" value={formatDateOnly(invoiceRow.canceledAt)} />
						<DetailItem label="Criado em" value={formatDateOnly(invoiceRow.createdAt)} />
						<DetailItem label="Atualizado em" value={formatDateOnly(invoiceRow.updatedAt)} />
					</Grid>

					<Grid item xs={12}>
						<SectionTitle title="Integração bancária" subtitle="Dados úteis do boleto quando disponíveis" />
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<DetailItem label="Linha digitável" value={invoiceRow.sicoobLinhaDigitavel || "-"} />
							</Grid>
							<Grid item xs={12} md={6}>
								<DetailItem label="Código de barras" value={invoiceRow.sicoobCodigoBarras || "-"} />
							</Grid>
							<Grid item xs={12} md={6}>
								<DetailItem label="Payment Link ID" value={invoiceRow.paymentLinkId || "-"} />
							</Grid>
							<Grid item xs={12} md={6}>
								<DetailItem label="Acquirer Payment ID" value={invoiceRow.acquirerPaymentId || "-"} />
							</Grid>
						</Grid>
					</Grid>
				</Grid>

				{invoiceLink && (
					<Box mt={3} display="flex" justifyContent="flex-end">
						<Button
							variant="outlined"
							endIcon={<OpenInNewIcon />}
							component={Link}
							href={invoiceLink}
							target="_blank"
							rel="noreferrer"
							underline="none"
						>
							Abrir boleto
						</Button>
					</Box>
				)}
			</DialogContent>
		</Dialog>
	);
};